/** Templates */
import bankAccountsTemplate from '../dom/help/BankAccounts.html';
import importPendingItemsTemplate from '../dom/help/ImportPendingItems.html';

/*******************************************************************************
 * @ngdoc service
 * @name  csm.books.InitialSetup.services.AppConfig
 *
 * @description
 * Configuration settings for module `csm.books.InitialSetup`. Settings may be
 * changed in application `.config` blocks.
 *
 * Adds `SERVER` and `AppConfig` constants to `$rootScope` to make settings
 * available to all template scopes from `$root`.
 *
 * Note: Configuration property settings are sealed so no properties can be
 *       added. Setting a property other than those listed here will fail
 *       silently or throw a TypeError if 'use strict' is set.
 *******************************************************************************/
/* @ngInject */ export function AppConfigProvider($injector) {
  var $prov = this;
  $prov.configProperties = Object.keys(Object.assign($prov, defaults()));
  $prov.$get = $injector.invoke(service, this, { $prov: $prov });
  Object.seal($prov);
}

/*******************************************************************************
 * defaults - Returns default settings for all configuration properties. No other
 * properties are added to the AppConfig service.
 *******************************************************************************/
/* @ngInject */ function defaults() {
  return {
    /**
     * @ngdoc        property
     * @name         allowOtherBankAcctItems
     * @description
     * When `true` user will be able to add outstanding bank items of type **Other**
     * in addition to **Checks** and **Deposits**. Default=`false`.
     */
    allowOtherBankAcctItems: false,

    /**
     * @ngdoc        property
     * @name         helpPages
     * @description
     * Object of help page URLs. Key is `pageViews[].id` property for page views or
     * other content identifiers used for dialog help.
     */
    helpPages: {
      BankAccounts: bankAccountsTemplate,
      ImportPendingItems: importPendingItemsTemplate
    },

    /**
     * @ngdoc        property
     * @name         hideAccountNumbers
     * @description
     * When `true`, Account Numbers are hidden from the user and not validated.
     * Default=`false`.
     */
    hideAccountNumbers: false,

    /**
     * @ngdoc        property
     * @name         pageViews
     * @description
     * List of page view definition objects in the order they will be shown to the user.
     * Objects include:
     *   * `{string}`**`id`**
     *     Page identifier. `$location.path()` value when page is visible.
     *
     *   * `{string}`**`pageTemplateID`**
     *     Page template ID in **`viewTemplatesURL`**.
     *
     *   * `{function}`**`condition`**
     *     Optional function called with `$injector.invoke()` and returning `true`
     *     If the page should be viewed. Else, page is skipped when traversing views.
     *
     *   * `{object}`**`$ctrl`**
     *     Properties to set on `<initial-setup>` controller for use in transcluded
     *     content when page is shown.
     */
    pageViews: null,

    /**
     * @ngdoc        property
     * @name         viewTemplatesURL
     * @description
     * URL of template with `<script type="ng-template">` blocks with page content
     * specified in `pageViews`. Template is preloaded and compiled to populate the
     * `$templateCache`.
     */
    viewTemplatesURL: null
  };
}

/*****************************************************************************
 * service */
/**
 * Finishes configuration and publishes configuration settings.
 *****************************************************************************/
/* @ngInject */ function service($prov) {
  return function ($compile, $http, $q, $rootScope, $templateCache, SERVER) {
    if (!$prov.helpPages) throw 'AppConfigProvider.helpPages not set.';
    if (!$prov.pageViews) throw 'AppConfigProvider.pageViews not set.';
    if (!$prov.viewTemplatesURL)
      throw 'AppConfigProvider.viewTemplatesURL not set.';

    /*------------------------------------------------*/
    /* Compile view templates to load $templateCache. */
    /*------------------------------------------------*/
    $http
      .get($prov.viewTemplatesURL, { cache: $templateCache })
      .then(function (r) {
        $compile(r.data);
      });

    /*----------------------------------------*/
    /* Make server constants available in     */
    /* all template scopes as `$root.SERVER`. */
    /*----------------------------------------*/
    $rootScope.SERVER = SERVER;

    /*----------------------------------------------*/
    /* Return configuration and make it available   */
    /* in all template scopes as `$root.AppConfig`. */
    /*----------------------------------------------*/
    return ($rootScope.AppConfig = $prov.configProperties.reduce(function (
      props,
      p
    ) {
      props[p] = $prov[p];
      return props;
    }, {}));
  };
}
